import { useState, type FormEvent } from 'react';
import { Eye, EyeOff, KeyRound } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useTenantUsers, useResetTenantUserPassword } from '@/hooks/useAdmin';
import type { AdminTenant, AdminTenantUser } from '@/api/admin.api';

const ROLE_LABEL: Record<string, string> = {
  owner: 'Proprietário',
  admin: 'Admin',
  operator: 'Operador',
  viewer: 'Visualizador',
};

interface ResetPasswordFormProps {
  tenantId: string;
  userId: string;
  userName: string;
  onDone: () => void;
}

function ResetPasswordForm({ tenantId, userId, userName, onDone }: ResetPasswordFormProps) {
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const { mutate: reset, isPending } = useResetTenantUserPassword();

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    reset({ tenantId, userId, password }, { onSuccess: onDone });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 pt-2 border-t">
      <p className="text-sm text-muted-foreground">
        Redefinir senha de <strong>{userName}</strong>
      </p>
      <div className="space-y-1">
        <Label htmlFor="new-password">Nova senha</Label>
        <div className="relative">
          <Input
            id="new-password"
            type={showPw ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={8}
            required
            className="pr-9"
            placeholder="Mínimo 8 caracteres"
          />
          <button
            type="button"
            className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
            onClick={() => setShowPw((v) => !v)}
          >
            {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          Cancelar
        </Button>
        <Button type="submit" size="sm" disabled={isPending || password.length < 8}>
          Salvar senha
        </Button>
      </div>
    </form>
  );
}

interface Props {
  tenant: AdminTenant | null;
  onOpenChange: (open: boolean) => void;
}

export function TenantUsersDialog({ tenant, onOpenChange }: Props) {
  const [resettingUserId, setResettingUserId] = useState<string | null>(null);
  const { data, isLoading } = useTenantUsers(tenant?.id ?? null);
  const users: AdminTenantUser[] = data ?? [];

  function handleClose() {
    setResettingUserId(null);
    onOpenChange(false);
  }

  return (
    <Dialog open={!!tenant} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Usuários — {tenant?.name}</DialogTitle>
          <DialogDescription>Gerencie os usuários e redefina senhas.</DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="space-y-2">
            {[1, 2].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        )}

        {!isLoading && (
          <div className="space-y-4">
            <div className="divide-y rounded-md border">
              {users.map((u) => (
                <div key={u.id} className="flex items-center justify-between px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{u.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-3">
                    <Badge variant="outline" className="text-xs">
                      {ROLE_LABEL[u.role] ?? u.role}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      title="Redefinir senha"
                      onClick={() => setResettingUserId(u.id === resettingUserId ? null : u.id)}
                    >
                      <KeyRound className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {resettingUserId && tenant && (
              <ResetPasswordForm
                tenantId={tenant.id}
                userId={resettingUserId}
                userName={users.find((u) => u.id === resettingUserId)?.name ?? ''}
                onDone={() => setResettingUserId(null)}
              />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
